"use client"

import { useEffect } from 'react'
import useSWR, { mutate } from 'swr'
import storage from '@utils/localStorage'
import checkLogin from '@utils/checkLogin'
import decodePayload from '@utils/jwt/decodePayload'
import checkIfExpired from '@utils/jwt/checkIfExpired'
import { destroySession, renewSession, scheduleRenewSession } from '@utils/jwt/sessionUtils'
import MessageHelper from '@utils/messageHelper'
import { tokenVersion } from '@api/auth/userToken'
import { CONTACT_PAGE, LOGIN_PAGE, LOGOUT_PAGE, USER_ACCOUNT } from '@constants/routes'
import { notify, notifyAfterNavigation } from 'lib/notice/notice'

const SessionController = () => {
    if (typeof window === 'undefined') return null

    const { data: user } = useSWR('user', storage)

    // Token unieważniony na serwerze (zmiana hasła, usunięte konto): wylogowanie w tej karcie
    useEffect(() => {
        const onExpired = (e) => {
            let stored = null
            try { stored = JSON.parse(localStorage.getItem('user') || 'null') } catch (err) { /* uszkodzony wpis */ }
            if (!checkLogin(stored)) return
            // inna karta tej przeglądarki ma już nowy token (zmiana hasła tutaj) — tylko synchronizacja
            const failed = e?.detail?.token
            if (failed && stored.token && failed !== stored.token) return mutate('user', stored)
            destroySession()
            if (e?.detail?.code === 'account_suspended') {
                const text = 'Konto zostało zawieszone. Jeśli to pomyłka albo chcesz wyjaśnić sprawę, napisz do nas.'
                const href = `${CONTACT_PAGE}?powod=${encodeURIComponent('Potrzebuję pomocy')}`
                const action = { label: 'Napisz do nas', href }
                // strony konta po wylogowaniu przekierowują (na stronę główną albo logowanie) — komunikat ma się pokazać także tam
                if (location.pathname.startsWith(USER_ACCOUNT)) notifyAfterNavigation(text, { action, only: ['/', LOGIN_PAGE] })
                if (!notify(text, { action })) MessageHelper.message(text)
                return
            }
            MessageHelper.message('Sesja wygasła. Zaloguj się ponownie.')
        }
        window.addEventListener('spl:session-expired', onExpired)
        return () => window.removeEventListener('spl:session-expired', onExpired)
    }, [])

    // klucz Akol zapisany przy wcześniejszym logowaniu — nie trzymamy go w przeglądarce
    useEffect(() => {
        if (!user || !('_akol_key' in user)) return
        const { _akol_key, ...rest } = user
        localStorage.setItem('user', JSON.stringify(rest))
        mutate('user', rest)
    }, [user])

    useEffect(() => {
        const isLoggedIn = checkLogin(user)
        if (isLoggedIn) {
            const payload = decodePayload(user?.token)
            const expired = checkIfExpired(null, payload)

            if (payload.tokenVersion != tokenVersion) {
                return window.location.replace(LOGOUT_PAGE)
            }

            if (expired) {
                renewSession(user)
            } else {
                scheduleRenewSession(user, payload.expires)
            }
        }
    }, [user])

    return null
}

export default SessionController